import React, { useMemo, useState } from 'react'
import { Chip, Paper, Stack } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { subMonths, subYears, startOfYear, isWithinInterval, isBefore, isAfter, addDays, format } from '@e3dc-react/shell/Libs/date'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ErrorSkeleton } from '@e3dc-react/shell/Elements/Error'
import { ChargingSessionModelV1 } from 'src/Libs/e-mobility/charging-sessions'
import ChargingSessionChart from './ChargingSessionChart'

const useStyles = makeStyles()(theme => ({
  chartContainer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(6),
  },
}))

function getStartDate(dateRange: ChartDateRangeOption): Date | undefined {
  const now = new Date()

  switch (dateRange) {
    case 'oneMonth': {
      return subMonths(now, 1)
    }
    case 'halfAYear': {
      return subMonths(now, 6)
    }
    case 'currentYear': {
      return startOfYear(now)
    }
    case 'oneYear': {
      return subYears(now, 1)
    }
    case 'all':
      return undefined
  }
}

const chartDateRangeOptions = ['oneMonth', 'halfAYear', 'currentYear', 'oneYear', 'all'] as const
type ChartDateRangeOption = (typeof chartDateRangeOptions)[number]

interface ChargingSessionChartContainerProps {
  chargingSessions: ChargingSessionModelV1[]
}

const ChargingSessionChartContainer: React.VFC<ChargingSessionChartContainerProps> = ({ chargingSessions }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const [dateRangeOption, setDateRangeOption] = useState<ChartDateRangeOption>('oneMonth')
  const startDate = getStartDate(dateRangeOption)

  const chartData = useMemo(() => {
    const now = new Date()
    // first, filter for sessions with actual energy data
    const chargingSessionsWithChargeEnergy = chargingSessions.filter(
      session => session.energyAll > 0 || session.energySolar > 0 || session.energyDischarged > 0
    )

    // then, filter for sessions inside the selected time range, skip filtering when "all" date range is selected for performance
    const chargingSessionInsideTimeRange =
      dateRangeOption === 'all'
        ? chargingSessionsWithChargeEnergy
        : chargingSessionsWithChargeEnergy.filter(session => {
            return startDate && session.startAt && isWithinInterval(new Date(session.startAt), { start: startDate, end: now })
          })

    // then, remove "time" extension from datetime string to condense sessions of the same day, regardless of exact time
    const chartDataByDate =
      chargingSessionInsideTimeRange?.map(session => ({
        // temporarily store date in common yyyy-MM-dd format for easier parsing, localization will happen inside chart tick formatter and tooltip formatter
        date: session.startAt ? format(new Date(session.startAt), 'yyyy-MM-dd') : '',
        total: session.energyAll,
        solar: session.energySolar,
        discharged: session.energyDischarged,
      })) || []

    const energySumByDate: Record<string, { total: number; solar: number; discharged: number; date: string }> = {}

    // if date entry already exists, add energy to existing sum for that specific day, otherwise create new date entry
    chartDataByDate.forEach(item => {
      if (!energySumByDate[item.date]) {
        energySumByDate[item.date] = {
          total: 0,
          solar: 0,
          discharged: 0,
          date: item.date,
        }
      }

      energySumByDate[item.date].total += item.total
      energySumByDate[item.date].solar += item.solar
      energySumByDate[item.date].discharged += item.discharged
    })

    if (chartDataByDate.length > 0) {
      // find min and max date which will be displayed in the chart
      const minDate = chartDataByDate.reduce((min, p) => (isBefore(new Date(p.date), new Date(min)) ? p.date : min), chartDataByDate[0].date)
      const maxDate = chartDataByDate.reduce((max, p) => (isAfter(new Date(p.date), new Date(max)) ? p.date : max), chartDataByDate[0].date)

      // then fill with empty data for missing dates between min and max date, e.g. to plot all 31 days of March when "current month" option is selected
      for (let currentDate = new Date(minDate); isBefore(currentDate, addDays(new Date(maxDate), 1)); currentDate = addDays(currentDate, 1)) {
        const date = format(currentDate, 'yyyy-MM-dd')

        if (!energySumByDate[date]) {
          energySumByDate[date] = {
            total: 0,
            solar: 0,
            discharged: 0,
            date,
          }
        }
      }
    }

    return Object.values(energySumByDate)
      .sort((a, b) => (isBefore(new Date(a.date), new Date(b.date)) ? -1 : 1))
      .map(item => ({
        /*
         * We round and display values with 2 decimal digits
         * Values under 0.005 are displayed in chart bar but when we round it for dynamic chart legend value is 0.00
         * We are displaying 0 for values under 0.005 for both chart bar and dynamic legend to stay consistence
         */
        total: Math.abs(item.total / 1000) < 0.005 ? 0 : item.total / 1000,
        solar: Math.abs(item.solar / 1000) < 0.005 ? 0 : item.solar / 1000,
        discharged: Math.abs(item.discharged / 1000) < 0.005 ? 0 : item.discharged / 1000,
        date: item.date,
      }))
  }, [chargingSessions, startDate, dateRangeOption])

  return (
    <Paper className={classes.chartContainer}>
      <Stack direction="row" rowGap={2} columnGap={2} flexWrap="wrap">
        {chartDateRangeOptions.map(option => (
          <Chip
            key={option}
            label={t(`emobility.chargingSession.dateRange.${option}`)}
            color={option === dateRangeOption ? 'primary' : 'default'}
            onClick={() => {
              setDateRangeOption(option)
            }}
          />
        ))}
      </Stack>

      {chartData.length === 0 ? <ErrorSkeleton title={t('common.noData')} type="barChart" /> : <ChargingSessionChart data={chartData} />}
    </Paper>
  )
}

export default ChargingSessionChartContainer
